import { Bike } from "lucide-react";
import { formatCurrency } from "@veorest/utils";
import { formatShortDateTime, type ReportDriverRow } from "../../lib/partnerReports";
import { useI18n } from "../../i18n";

interface DriverBreakdownTableProps {
  rows: ReportDriverRow[];
  loading: boolean;
  /** 170: per-driver cost in range (driver_id → IQD). Column hidden when absent. */
  costById?: Map<string, number>;
}

export function DriverBreakdownTable({ rows, loading, costById }: DriverBreakdownTableProps) {
  const { t, locale } = useI18n();
  const showCost = costById != null;
  const colCount = showCost ? 5 : 4;
  const money = (v: number) => formatCurrency(v, "IQD", locale);

  return (
    <div className="report-table-wrap">
      <table className="table-flat">
        <thead>
          <tr>
            <th>{t("analytics.colDriver")}</th>
            <th className="text-end">{t("analytics.colTrips")}</th>
            <th className="text-end">{t("analytics.colKm")}</th>
            {showCost ? (
              <th className="text-end">{t("analytics.colDriverCost")}</th>
            ) : null}
            <th>{t("analytics.colLastTrip")}</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colCount} className="py-10 text-center text-sm text-[var(--text-muted)]">
                {t("common.loading")}
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="py-10 text-center">
                <Bike className="mx-auto h-7 w-7 text-[var(--text-muted)]" />
                <p className="mt-2 text-sm text-[var(--text-muted)]">
                  {t("analytics.empty")}
                </p>
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.driver_id}>
                <td className="font-semibold text-[var(--text-primary)]">{row.name}</td>
                <td className="text-end tabular">{row.trips}</td>
                <td className="text-end tabular">
                  {row.km > 0 ? row.km.toFixed(1) : "—"}
                </td>
                {showCost ? (
                  <td className="text-end tabular">
                    {(() => {
                      const cost = costById?.get(row.driver_id);
                      return cost != null && cost > 0 ? money(cost) : "—";
                    })()}
                  </td>
                ) : null}
                <td className="text-sm text-[var(--text-secondary)]">
                  {formatShortDateTime(row.last_trip_at)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
